import { Work } from "@design-system";

export type GenericRange<T = number> = {
  lower?: T | null;
  upper?: T | null;
};

export const rangeUtils = {
  // Get a number value from a range or single value
  getValue: (val: any, key: "lower" | "upper" = "lower"): number => {
    if (typeof val === "number") return val;
    if (val && typeof val === "object" && key in val && typeof (val as any)[key] === "number")
      return (val as any)[key];
    return 0;
  },

  // Format a range value with a formatter function
  formatRange: (
    lowerInput: number | null | undefined,
    upperInput: number | null | undefined,
    formatter: (v: number) => string
  ): string => {
    let lower = lowerInput;
    let upper = upperInput;

    // Ensure lower <= upper if both are defined
    if (lower != null && upper != null && lower > upper) {
      [lower, upper] = [upper, lower]; // Swap values
    }

    if (lower == null && upper == null) return "-";
    if (lower == null) return formatter(upper!);
    if (upper == null) return formatter(lower);
    if (Math.round(lower * 1000) === Math.round(upper * 1000)) return formatter(lower);
    return `${formatter(lower)} to ${formatter(upper)}`;
  },

  // Collapse a range object to a string
  collapseRangeObject: (val: any, formatter: (v: number) => string): string => {
    if (typeof val === "number") return formatter(val);
    if (val && typeof val === "object") {
      let { lower, upper } = val;

      // Ensure lower <= upper if both are defined
      if (lower != null && upper != null && lower > upper) {
        [lower, upper] = [upper, lower]; // Swap values
      }
      return rangeUtils.formatRange(lower, upper, formatter);
    }
    return "-";
  },

  // Calculate GPM from fee and cost
  calculateGpm: (fee: number, cost: number): number => {
    return fee > 0 ? ((fee - cost) / fee) * 100 : 0;
  },

  // Format GPM from fee and cost ranges
  formatGpmRange: (fee: GenericRange, cost: GenericRange): string => {
    let lowerFee = fee?.lower;
    let upperFee = fee?.upper;
    let lowerCost = cost?.lower;
    let upperCost = cost?.upper;

    // Ensure lower <= upper for fee range
    if (lowerFee != null && upperFee != null && lowerFee > upperFee) {
      [lowerFee, upperFee] = [upperFee, lowerFee];
    }

    // Ensure lower <= upper for cost range
    if (lowerCost != null && upperCost != null && lowerCost > upperCost) {
      [lowerCost, upperCost] = [upperCost, lowerCost];
    }

    // Use 0 as default if null/undefined after potential swap
    const finalLowerFee = lowerFee ?? 0;
    const finalUpperFee = upperFee ?? 0;
    const finalLowerCost = lowerCost ?? 0;
    const finalUpperCost = upperCost ?? 0;

    const gpmLower = rangeUtils.calculateGpm(finalLowerFee, finalLowerCost);
    const gpmUpper = rangeUtils.calculateGpm(finalUpperFee, finalUpperCost);

    // formatRange will handle swapping gpmLower/gpmUpper if needed
    return rangeUtils.formatRange(
      finalLowerFee > 0 ? gpmLower : null,
      finalUpperFee > 0 ? gpmUpper : null,
      (v) => `${Math.round(v)}%`
    );
  },

  // Check if work items have ranges
  hasRangeInWork: (work: Work[] | null | undefined): boolean => {
    if (!work?.length) return false;
    return work.some(
      (w) =>
        (w.price?.lower != null && w.price?.upper != null && w.price.lower !== w.price.upper) ||
        (w.cost?.lower != null && w.cost?.upper != null && w.cost.lower !== w.cost.upper)
    );
  },
};
